/**
 * Reads the auto-host's clue aloud on this client (#91, D1/D2).
 *
 * When the authoritative state enters `READING` for a clue this client hasn't yet
 * read, it resolves the clue's read-aloud segments ({@link resolveSpeechSegments} —
 * the generator-emitted `speech`, or a heuristic derivation from the prompt) and
 * speaks them; on completion it sends the **advisory** `read_complete` (the server
 * opens the buzzer at its own fair moment — a fast/slow local voice can't change it).
 *
 * Driven off authoritative `clueState.status`, not the transient `clue_reading`
 * event, so it survives a resync and never double-fires (a ref tracks the clue we've
 * already started). A no-op in `HUMAN` games and where TTS is unsupported (the synth
 * resolves immediately, and `read_complete` is still sent so the server's all-clients
 * check isn't stalled by this client).
 *
 * It ALSO reads the correct answer aloud at end-of-clue (#192, FR-4.10): when the
 * clue holds in `RESOLVED` on the unanswered path, the server un-blanks its response
 * to all roles, and this hook speaks it through the same (Polly-backed) synthesizer.
 * Fire-and-forget — there is no completion signal to send (the reveal hold is a fixed
 * server window, not gated on client audio).
 */

import { useEffect, useRef } from 'react';
import { resolveSpeechSegments, type Command, type GameState } from '@jeopardy/shared';
import { currentClue } from '../game/selectors';
import {
  answerBeingRead,
  clueBeingRead,
  finalAnswerBeingRead,
} from '../game/voice-selectors';
import { getSpeechSynthesizer, type SpeechSynthesizer } from './speech-synth';

export interface UseClueReaderParams {
  state: GameState | null;
  send: (command: Command) => void;
  /** Injectable for tests; defaults to the shared browser synthesizer. */
  synthesizer?: SpeechSynthesizer;
}

export function useClueReader({ state, send, synthesizer }: UseClueReaderParams): void {
  // The clue we've already begun reading, so a re-render / version bump doesn't
  // restart the speech mid-read.
  const readingClueRef = useRef<string | null>(null);

  const clueId = clueBeingRead(state);

  useEffect(() => {
    if (!state || clueId === null) return;
    if (readingClueRef.current === clueId) return;
    const clue = currentClue(state);
    if (!clue) return;

    // Resolve the synthesizer HERE, at read time — never once at mount. `/api/config`
    // (which enables the backend Polly adapter, #93) resolves asynchronously AFTER the
    // page mounts, and `configureVoiceBackend` then re-selects the factory singleton. A
    // clue is only ever read once a game is live (well after config), so selecting now
    // always yields the backend synthesizer when the deployment advertises it. Caching
    // it in a mount-time `useMemo` would pin the pre-config browser synthesizer for the
    // component's whole life and never hit `/api/voice/tts`.
    const synth = synthesizer ?? getSpeechSynthesizer();
    readingClueRef.current = clueId;
    const segments = resolveSpeechSegments(clue.speech, clue.prompt);
    // #99 (NFR-6.9): if the clue carries a pre-generated audio ref (present only
    // while it's being read aloud — the view gates it), play that cached clip
    // directly instead of an on-demand `/api/voice/tts` synthesis; on any failure
    // it falls back to on-demand synthesis then the browser voice.
    const audioRef = clue.audioRef;
    let cancelled = false;
    const read = audioRef
      ? synth.speakClueFromUrl(audioRef, segments)
      : synth.speakClue(segments);
    void read.then(() => {
      if (!cancelled) send({ type: 'read_complete', clueId });
    });
    return () => {
      cancelled = true;
    };
  }, [state, clueId, send, synthesizer]);

  // Once the clue is no longer being read, forget it so a later re-read (a fresh
  // clue that happens to reuse an id after recovery) can fire again.
  useEffect(() => {
    if (clueId === null) readingClueRef.current = null;
  }, [clueId]);

  // --- #192 (FR-4.10): read the revealed answer aloud on the unanswered path ---
  const readingAnswerRef = useRef<string | null>(null);
  const answerClueId = answerBeingRead(state);

  useEffect(() => {
    if (!state || answerClueId === null) return;
    if (readingAnswerRef.current === answerClueId) return;
    const clue = currentClue(state);
    const response = clue?.response ?? '';
    if (response.length === 0) return;

    const synth = synthesizer ?? getSpeechSynthesizer();
    readingAnswerRef.current = answerClueId;
    // Derive read-aloud segments from the revealed response (no authored answer
    // speech exists); fire-and-forget — the reveal hold is a fixed server window.
    const segments = resolveSpeechSegments(undefined, response);
    void synth.speakClue(segments);
  }, [state, answerClueId, synthesizer]);

  useEffect(() => {
    if (answerClueId === null) readingAnswerRef.current = null;
  }, [answerClueId]);

  // --- #205 (FR-6.6): read the Final Peril correct response aloud ---
  // In an AUTO game, once the Final response is un-blanked (FINAL_JUDGING through the
  // FINAL_REVEAL linger), speak it once through the same Polly-backed synthesizer.
  // Fire-and-forget — the reveal linger is a fixed server window (stretched to fit
  // the spoken answer), not gated on client audio. A no-op in HUMAN games (the
  // selector returns null). The ref guards against re-reading on a version bump.
  const readingFinalRef = useRef<string | null>(null);
  const finalResponse = finalAnswerBeingRead(state);

  useEffect(() => {
    if (!state || finalResponse === null) return;
    if (readingFinalRef.current === finalResponse) return;

    const synth = synthesizer ?? getSpeechSynthesizer();
    readingFinalRef.current = finalResponse;
    const segments = resolveSpeechSegments(undefined, finalResponse);
    void synth.speakClue(segments);
  }, [state, finalResponse, synthesizer]);

  useEffect(() => {
    if (finalResponse === null) readingFinalRef.current = null;
  }, [finalResponse]);
}
